import React from "react";
import { Box } from "@mui/material";

type Props = {
    value: string;
    onChange: (color: string) => void;
    size?: number;
    ariaLabel?: string;
};

// A plain native color input shown as a swatch. Deliberately NOT a MUI
// TextField type="color": inside InputBase, the native picker's flood of
// change events while dragging (plus InputBase refocusing its own input on
// every click) set off a chain of state updates that hit React's "Maximum
// update depth exceeded". Changes are applied at most once per animation
// frame - the latest color wins - so dragging around the picker stays cheap.
const ColorSwatchInput: React.FC<Props> = ({ value, onChange, size = 40, ariaLabel = 'Pick color' }) => {
    const pendingRef = React.useRef<string | null>(null);
    const frameRef = React.useRef<number | null>(null);
    const onChangeRef = React.useRef(onChange);
    onChangeRef.current = onChange;

    React.useEffect(() => () => {
        if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        pendingRef.current = e.target.value;
        if (frameRef.current !== null) return;
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            if (pendingRef.current) onChangeRef.current(pendingRef.current);
        });
    };

    return (
        <Box
            component="input"
            type="color"
            value={value}
            onChange={handleChange}
            aria-label={ariaLabel}
            sx={{
                flexShrink: 0,
                width: size,
                height: size,
                p: 0.5,
                border: '1px solid rgba(0,0,0,0.23)',
                borderRadius: 1.5,
                cursor: 'pointer',
                background: '#fff',
                boxSizing: 'border-box',
                '&::-webkit-color-swatch-wrapper': { p: 0 },
                '&::-webkit-color-swatch': { border: 'none', borderRadius: 0.75 },
                '&::-moz-color-swatch': { border: 'none', borderRadius: 0.75 },
            }}
        />
    );
};

export default ColorSwatchInput;
